// The one transport, and the policy every request carries.
//
// The generated client under ./gen knows the routes and the shapes. What it
// does not know is decided here, once: where the API is mounted, how the
// caller proves who they are, and what a 401 means.
//
// In production the bearer is the access token the app's own accounts minted
// (lib/auth.ts). In development and tests the server runs its header verifier,
// and the caller names their subject (their address) in X-Test-Subject
// instead. Which one is in force is a build-time setting (VITE_AUTH_MODE),
// never a runtime one, so a production bundle cannot be talked into the seam.

import { AUTH_MODE, freshAccessToken, refresh } from '../lib/auth';
import { client } from './gen/client.gen';

/** Where the API is mounted. The dev server proxies this to the Go binary. */
export const BASE = '/api';

/** Thrown for any non-2xx response, carrying the server's own message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Problem,
  ) {
    super(problem.message || problem.detail || problem.title || `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

/** The error shape the server answers with: humaapi's model. */
export interface Problem {
  code?: string;
  message?: string;
  title?: string;
  detail?: string;
  details?: { message: string; location?: string }[];
}

let configured = false;
let handlers = { renewed: () => {}, unauthenticated: () => {} };

/**
 * Points the generated client at the API and gives it the caller's
 * credential, read per request because a renewal replaces it mid-session.
 * A 401 is one chance to renew; a second refusal signs the browser out.
 */
export function configureClient(onRenewed: () => void, onUnauthenticated: () => void): void {
  // Interceptors accumulate, and React runs an effect twice in development,
  // so they are registered once and only the handlers are replaced.
  handlers = { renewed: onRenewed, unauthenticated: onUnauthenticated };
  if (configured) return;
  configured = true;
  client.setConfig({ baseUrl: BASE });
  client.interceptors.request.use(async (request) => {
    const credential = await freshAccessToken();
    if (credential) {
      if (AUTH_MODE === 'header') request.headers.set('X-Test-Subject', credential);
      else request.headers.set('Authorization', `Bearer ${credential}`);
    }
    return request;
  });
  client.interceptors.response.use(async (response) => {
    if (response.status === 401) {
      if (await refresh()) handlers.renewed();
      else handlers.unauthenticated();
    }
    return response;
  });
}

/**
 * Unwraps a generated call into its value, or throws {@link ApiError}. The
 * SDK answers `{ data, error }` rather than throwing, which TanStack Query
 * would report as success.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response?: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response) {
    throw new ApiError(0, { title: 'Network error', detail: String(error ?? 'request failed') });
  }
  if (!response.ok) {
    throw new ApiError(response.status, (error as Problem) ?? {});
  }
  return data as T;
}
